import { useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { LeaveForm, STATUS_CLS } from "@/components/LeaveWidgets";
import { fmtDate, fmtDateTime } from "@/lib/helpers";
import { CalendarDays, ChevronDown, Euro, Loader2, MessageSquare, Send, UserRound, XCircle } from "lucide-react";

export const KIND_META = {
  leave: { label: "Congé / absence", icon: CalendarDays, cls: "bg-brand-50 text-brand-800 border-brand-200" },
  acompte: { label: "Acompte sur salaire", icon: Euro, cls: "bg-amber-50 text-amber-800 border-amber-200" },
  rdv_interne: { label: "Rendez-vous interne", icon: UserRound, cls: "bg-violet-50 text-violet-800 border-violet-200" },
  diverse: { label: "Demande diverse", icon: MessageSquare, cls: "bg-slate-100 text-slate-700 border-slate-200" },
};
export const STATUS_CLS_ALL = { ...STATUS_CLS, accepte: STATUS_CLS.acceptee, refuse: STATUS_CLS.refusee, verse: "bg-green-600 text-white border-green-700", terminee: "bg-slate-200 text-slate-700 border-slate-300", traitee: "bg-green-100 text-green-800 border-green-200" };
const today = () => new Date().toISOString().slice(0, 10);
// Congé « en cours » pour le salarié : en attente, ou accepté et pas encore terminé.
export const leaveIsActive = (l) => l.status === "en_attente" || ((l.status === "acceptee" || l.status === "corrigee") && (l.ongoing || l.end_date >= today()));
export const kindOf = (item) => item.kind || "leave";
export const titleOf = (item) => (item.kind ? item.summary : `${item.type_label || "Congé"} du ${fmtDate(item.start_date)} ${item.ongoing ? "— en cours" : `au ${fmtDate(item.end_date)} · ${item.days} j`}`);
const monthDefault = () => today().slice(0, 7);

// Choix du type puis formulaire correspondant.
export function NewRequestChooser({ st, isAdmin, onCreated, prefix = "newreq" }) {
  const [kind, setKind] = useState(null);
  const kinds = [...(st.enabled ? ["leave"] : []), ...(st.staff_requests_enabled ? ["acompte", "rdv_interne", "diverse"] : [])];
  return (
    <div className="space-y-3" data-testid={`${prefix}-chooser`}>
      <div className="grid grid-cols-2 gap-2">
        {kinds.map((k) => { const M = KIND_META[k]; return <button key={k} type="button" data-testid={`${prefix}-kind-${k}`} onClick={() => setKind(k)} className={`h-14 rounded-xl border-2 text-sm font-bold px-2 inline-flex items-center gap-2 justify-center ${kind === k ? "border-accent-500 bg-accent-50 text-accent-800" : "border-slate-200 text-slate-700 bg-white"}`}><M.icon className="w-4 h-4" />{M.label}</button>; })}
      </div>
      {kind === "leave" && <LeaveForm minNotice={st.min_notice_days} isAdmin={isAdmin} prefix={`${prefix}-leave`} onCreated={(d) => { setKind(null); onCreated?.(d); }} />}
      {kind && kind !== "leave" && <StaffRequestForm kind={kind} prefix={`${prefix}-${kind}`} onCreated={(d) => { setKind(null); onCreated?.(d); }} />}
    </div>
  );
}

// Formulaires minimaux : acompte (montant, mois, commentaire) · RDV interne (objet, destinataire, motif, disponibilités) · diverse (objet, message).
export function StaffRequestForm({ kind, onCreated, prefix = "sr" }) {
  const [f, setF] = useState({ amount: "", month: monthDefault(), comment: "", subject: "", recipient: "direction", message: "", availability: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    if (kind === "acompte" && (!f.amount || !f.month)) return toast.error("Montant et mois concerné obligatoires");
    if (kind !== "acompte" && (!f.subject.trim() || !f.message.trim())) return toast.error(kind === "rdv_interne" ? "Objet et motif obligatoires" : "Objet et message obligatoires");
    setBusy(true);
    try { const { data } = await api.post("/staff-requests", { kind, ...f, amount: f.amount ? Number(String(f.amount).replace(",", ".")) : null }); toast.success("Demande envoyée — en attente de traitement"); onCreated?.(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-4" data-testid={`${prefix}-form`}>
      <p className="text-sm font-bold text-brand-900">{KIND_META[kind].label}</p>
      {kind === "acompte" && <>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Montant demandé (€) *</Label><Input data-testid={`${prefix}-amount`} inputMode="decimal" placeholder="300" value={f.amount} onChange={set("amount")} className="mt-1 h-11" /></div>
          <div><Label>Mois concerné *</Label><Input type="month" data-testid={`${prefix}-month`} value={f.month} onChange={set("month")} className="mt-1 h-11" /></div>
        </div>
        <div><Label>Commentaire (facultatif)</Label><Textarea data-testid={`${prefix}-comment`} value={f.comment} onChange={set("comment")} rows={2} className="mt-1" /></div>
      </>}
      {kind === "rdv_interne" && <>
        <div><Label>Objet *</Label><Input data-testid={`${prefix}-subject`} placeholder="Ex : mon planning" value={f.subject} onChange={set("subject")} className="mt-1 h-11" /></div>
        <div><Label>Destinataire</Label><div className="grid grid-cols-2 gap-2 mt-1">{[["direction", "Direction"], ["administration", "Administration"]].map(([v, l]) => <button key={v} type="button" data-testid={`${prefix}-recipient-${v}`} onClick={() => setF({ ...f, recipient: v })} className={`h-11 rounded-xl border-2 text-sm font-bold ${f.recipient === v ? "border-accent-500 bg-accent-50 text-accent-800" : "border-slate-200 text-slate-600"}`}>{l}</button>)}</div></div>
        <div><Label>Motif ou message *</Label><Textarea data-testid={`${prefix}-message`} placeholder="Je souhaiterais un rendez-vous concernant…" value={f.message} onChange={set("message")} rows={3} className="mt-1" /></div>
        <div><Label>Disponibilités / précision (facultatif)</Label><Input data-testid={`${prefix}-availability`} placeholder="Ex : mardi ou jeudi après 14 h" value={f.availability} onChange={set("availability")} className="mt-1 h-11" /></div>
      </>}
      {kind === "diverse" && <>
        <div><Label>Objet *</Label><Input data-testid={`${prefix}-subject`} placeholder="Ex : attestation employeur" value={f.subject} onChange={set("subject")} className="mt-1 h-11" /></div>
        <div><Label>Message *</Label><Textarea data-testid={`${prefix}-message`} value={f.message} onChange={set("message")} rows={3} className="mt-1" /></div>
      </>}
      <Button data-testid={`${prefix}-submit`} onClick={submit} disabled={busy} className="w-full h-12 bg-accent-600 hover:bg-accent-700">{busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5 mr-2" />}Envoyer la demande</Button>
    </div>
  );
}

export function KindBadge({ kind }) {
  const M = KIND_META[kind];
  return <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full border inline-flex items-center gap-1 ${M.cls}`}><M.icon className="w-3 h-3" />{M.label}</span>;
}

// Détail d'une demande (payload + réponse), commun salarié / admin.
export function RequestDetails({ item }) {
  const p = item.payload || {};
  return (
    <div className="text-sm text-slate-700 space-y-1 mt-2">
      {item.kind === "acompte" && p.comment && <p className="italic text-slate-500">« {p.comment} »</p>}
      {item.kind === "rdv_interne" && <><p className="text-xs text-slate-500">Destinataire : {p.recipient === "administration" ? "Administration" : "Direction"}</p><p>{p.message}</p>{p.availability && <p className="text-xs text-slate-500">Disponibilités : {p.availability}</p>}</>}
      {item.kind === "diverse" && <p>{p.message}</p>}
      {!item.kind && item.comment && <p className="italic text-slate-500">« {item.comment} »</p>}
      {item.decision_comment && <p className="bg-slate-50 rounded-lg p-2 mt-1">Réponse : {item.decision_comment}</p>}
      {item.decided_by_name && item.status !== "en_attente" && <p className="text-xs text-slate-400">Traité par {item.decided_by_name} le {fmtDateTime(item.decided_at)}</p>}
    </div>
  );
}

// Carte unifiée (congé ou demande interne). compact = une ligne cliquable pour l'historique.
export function RequestCard({ item, compact, showUser, onCancel, actions, prefix = "req" }) {
  const [open, setOpen] = useState(false);
  const kind = kindOf(item);
  if (compact) {
    return (
      <div data-testid={`${prefix}-compact-${item.id}`} className="bg-white rounded-lg border border-slate-200 px-3 py-2">
        <button type="button" onClick={() => setOpen(!open)} className="w-full text-left flex items-center gap-2 flex-wrap">
          <KindBadge kind={kind} /><span className="text-sm font-semibold text-slate-800 truncate flex-1 min-w-[120px]">{showUser ? `${item.user_name} — ` : ""}{titleOf(item)}</span>
          <span className="text-xs text-slate-400">{fmtDate(item.created_at)}</span>
          <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full border ${STATUS_CLS_ALL[item.status]}`}>{item.status_label}</span>
        </button>
        {open && <RequestDetails item={item} />}
      </div>
    );
  }
  return (
    <div data-testid={`${prefix}-${item.id}`} className={`bg-white rounded-xl border p-3 ${item.status === "en_attente" ? "border-orange-200 shadow-sm" : "border-slate-200"}`}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap"><KindBadge kind={kind} />{showUser && <span className="text-xs text-slate-500">{item.user_name}</span>}</div>
          <p className="font-bold text-slate-800 mt-1">{titleOf(item)}</p>
          <p className="text-xs text-slate-500">déposée le {fmtDate(item.created_at)}{item.override ? " · dérogation" : ""}{!item.kind && !item.respects_notice ? " · hors délai" : ""}</p>
        </div>
        <span data-testid={`${prefix}-status-${item.id}`} className={`text-xs font-bold px-2 py-1 rounded-full border ${STATUS_CLS_ALL[item.status]}`}>{item.status_label}</span>
      </div>
      <RequestDetails item={item} />
      {actions}
      {onCancel && item.status === "en_attente" && <button data-testid={`${prefix}-cancel-${item.id}`} onClick={() => onCancel(item)} className="mt-2 text-sm font-bold text-slate-500 inline-flex items-center gap-1"><XCircle className="w-4 h-4" />Annuler la demande</button>}
      {!item.kind && item.status === "acceptee" && !showUser && <p className="text-xs text-slate-400 mt-2">Pour annuler un congé accepté, contactez votre administrateur.</p>}
    </div>
  );
}

// Section repliée par défaut : « Titre (N) ▾ ».
export function CollapsedSection({ title, count, testid, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={testid} className="rounded-xl border border-slate-200 bg-slate-50/60">
      <button type="button" data-testid={`${testid}-toggle`} aria-expanded={open} onClick={() => setOpen(!open)} className="w-full h-12 px-4 flex items-center justify-between text-sm font-bold text-slate-700">
        <span>{title} ({count})</span><ChevronDown className={`w-5 h-5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-3 pb-3 space-y-1.5">{count === 0 ? <p className="text-sm text-slate-400 text-center py-3">Aucune demande traitée.</p> : children}</div>}
    </div>
  );
}

// Vue salarié : « Mes demandes en cours » (congés + demandes internes) puis « Mes demandes traitées (N) » repliée.
export function MyRequestsView({ leaves, staff, onChanged, prefix = "my" }) {
  const all = [...leaves, ...staff].sort((a, b) => (b.created_at > a.created_at ? 1 : -1));
  const active = all.filter((i) => (i.kind ? !i.is_final : leaveIsActive(i)));
  const done = all.filter((i) => !active.includes(i));
  const cancel = async (item) => {
    try { await api.post(item.kind ? `/staff-requests/${item.id}/cancel` : `/leaves/${item.id}/cancel`); toast.success("Demande annulée"); onChanged?.(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || e.message); }
  };
  return (
    <div className="space-y-4" data-testid={`${prefix}-requests`}>
      <div>
        <h2 className="text-base md:text-lg font-heading font-bold text-brand-900 mb-2" data-testid={`${prefix}-active-title`}>Mes demandes en cours{active.length ? ` (${active.length})` : ""}</h2>
        {active.length === 0 ? <p className="text-sm text-slate-400 bg-white rounded-xl border border-slate-200 p-4 text-center" data-testid={`${prefix}-empty`}>Aucune demande en cours.</p>
          : <div className="space-y-2">{active.map((i) => <RequestCard key={i.id} item={i} onCancel={cancel} prefix={`${prefix}-req`} />)}</div>}
      </div>
      <CollapsedSection title="Mes demandes traitées" count={done.length} testid={`${prefix}-done`}>{done.map((i) => <RequestCard key={i.id} item={i} compact prefix={`${prefix}-req`} />)}</CollapsedSection>
    </div>
  );
}
